import { type UpdateStoredViewInput, type UpdateStoredViewMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type ActiveView } from "../../models/active-view-model";

import { updateStoredViewMutationDocument } from "./stored-view.gql";
import { EVENT_STORED_VIEW_UPDATED, trackStoredViewEvent } from "./tracking";

type UpdatedStoredView = UpdateStoredViewMutation["updateStoredView"];

export interface UseUpdateStoredViewProps {
  onCompleted?: (data: UpdatedStoredView) => void;
  onError?: (e: Error) => void;
}

export const useUpdateStoredView = ({ onCompleted, onError }: UseUpdateStoredViewProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateStoredViewInput) => {
      const client = createOperationClient("UpdateStoredView");
      const data = await client.request(updateStoredViewMutationDocument, { input });
      return data.updateStoredView;
    },
    onSuccess: (updatedStoredView) => {
      trackStoredViewEvent(EVENT_STORED_VIEW_UPDATED, updatedStoredView);

      // Invalidate stored view list to refetch with updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.storedViews.list(updatedStoredView.key, updatedStoredView.workspaceId),
      });

      // Update active view cache if it references this stored view
      const activeViewQueryKey = queryKeys.activeViews.detail(updatedStoredView.key, updatedStoredView.workspaceId);
      const existingActiveView = queryClient.getQueryData(activeViewQueryKey);

      if (existingActiveView && (existingActiveView as ActiveView).storedView?.id === updatedStoredView.id) {
        // Update the active view cache with the updated stored view
        queryClient.setQueryData(activeViewQueryKey, (oldData: ActiveView | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            storedView: updatedStoredView,
          };
        });
      } else {
        // If we can't find the active view in cache, invalidate to refetch
        queryClient.invalidateQueries({
          queryKey: activeViewQueryKey,
        });
      }

      // If this is a default view, we need to update other stored views to not be default
      if (updatedStoredView.default) {
        // Invalidate all stored view lists for this workspace to ensure default states are correct
        queryClient.invalidateQueries({
          queryKey: queryKeys.storedViews.lists(),
        });
      }

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(updatedStoredView);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateStoredView: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
